/**
 * RichContent — parse text containing [video]URL[/video] tags and render
 * a mix of paragraphs and responsive video embeds (YouTube, Vimeo, or MP4).
 * Used by BlogPost and VehicleDetail.
 */

export function parseContent(text) {
  if (!text) return [];
  const parts = text.split(/(\[video\][\s\S]*?\[\/video\])/g).filter(Boolean);
  const blocks = [];
  parts.forEach((part, i) => {
    const videoMatch = part.match(/^\[video\]([\s\S]*?)\[\/video\]$/);
    if (videoMatch) {
      blocks.push({ type: "video", url: videoMatch[1].trim(), key: `v-${i}` });
    } else {
      part.split(/\n\n+/).map((p) => p.trim()).filter(Boolean).forEach((p, j) => {
        blocks.push({ type: "p", text: p, key: `p-${i}-${j}` });
      });
    }
  });
  return blocks;
}

export function VideoEmbed({ url }) {
  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  if (ytMatch) {
    return (
      <div className="my-8 relative aspect-video rounded-md overflow-hidden bg-black shadow-[0_16px_48px_-24px_rgba(0,0,0,0.6)]">
        <iframe
          data-testid="rc-video-youtube"
          src={`https://www.youtube-nocookie.com/embed/${ytMatch[1]}?rel=0`}
          title="Vidéo YouTube"
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          loading="lazy"
          className="absolute inset-0 w-full h-full border-0"
        />
      </div>
    );
  }
  const vmMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vmMatch) {
    return (
      <div className="my-8 relative aspect-video rounded-md overflow-hidden bg-black shadow-[0_16px_48px_-24px_rgba(0,0,0,0.6)]">
        <iframe
          data-testid="rc-video-vimeo"
          src={`https://player.vimeo.com/video/${vmMatch[1]}?title=0&byline=0&portrait=0`}
          title="Vidéo Vimeo"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          loading="lazy"
          className="absolute inset-0 w-full h-full border-0"
        />
      </div>
    );
  }
  return (
    <div className="my-8 relative aspect-video rounded-md overflow-hidden bg-black shadow-[0_16px_48px_-24px_rgba(0,0,0,0.6)]">
      <video
        data-testid="rc-video-native"
        src={url}
        controls
        preload="metadata"
        playsInline
        className="absolute inset-0 w-full h-full object-contain bg-black"
      >
        Votre navigateur ne supporte pas la lecture vidéo. <a href={url} className="text-[#C8A75A] underline">Télécharger la vidéo</a>.
      </video>
    </div>
  );
}

export default function RichContent({ text, paragraphClassName = "" }) {
  const blocks = parseContent(text);
  return (
    <>
      {blocks.map((b) =>
        b.type === "video" ? (
          <VideoEmbed key={b.key} url={b.url} />
        ) : (
          <p key={b.key} className={`whitespace-pre-line ${paragraphClassName}`}>{b.text}</p>
        )
      )}
    </>
  );
}
